import {
  Color3,
  Color4,
  DirectionalLight,
  HemisphericLight,
  Scene,
  Vector3,
} from '@babylonjs/core';

export type TimeOfDayState = 'day' | 'dusk' | 'night';

export interface TimeOfDayController {
  get(): TimeOfDayState;
  set(state: TimeOfDayState): void;
  cycle(): void;
}

interface Preset {
  sunIntensity: number;
  sunDiffuse: Color3;
  sunDirection: Vector3;
  ambientIntensity: number;
  ambientDiffuse: Color3;
  clearColor: Color4;
  fogColor: Color3;
}

const PRESETS: Record<TimeOfDayState, Preset> = {
  day: {
    sunIntensity: 1.1,
    sunDiffuse: new Color3(1.0, 0.96, 0.88),
    sunDirection: new Vector3(-0.5, -1, -0.3).normalize(),
    ambientIntensity: 0.55,
    ambientDiffuse: new Color3(0.85, 0.9, 1.0),
    clearColor: new Color4(0.62, 0.78, 0.92, 1.0),
    fogColor: new Color3(0.62, 0.78, 0.92),
  },
  // Golden-hour: warmer and lower-angled than day, but still bright. Sits between day and night.
  dusk: {
    sunIntensity: 0.8,
    sunDiffuse: new Color3(1.0, 0.8, 0.6),
    sunDirection: new Vector3(-0.7, -0.6, -0.3).normalize(),
    ambientIntensity: 0.42,
    ambientDiffuse: new Color3(0.9, 0.78, 0.7),
    clearColor: new Color4(0.85, 0.7, 0.6, 1.0),
    fogColor: new Color3(0.85, 0.7, 0.6),
  },
  // Sunset / late dusk: low orange sun, pink-orange sky. Dim enough that headlights matter.
  night: {
    sunIntensity: 0.5,
    sunDiffuse: new Color3(1.0, 0.55, 0.3),
    sunDirection: new Vector3(-0.8, -0.3, -0.3).normalize(),
    ambientIntensity: 0.3,
    ambientDiffuse: new Color3(0.9, 0.6, 0.5),
    clearColor: new Color4(0.95, 0.5, 0.35, 1.0),
    fogColor: new Color3(0.95, 0.5, 0.35),
  },
};

const ORDER: TimeOfDayState[] = ['day', 'dusk', 'night'];

export function createTimeOfDayController(
  scene: Scene,
  sun: DirectionalLight,
  ambient: HemisphericLight,
): TimeOfDayController {
  let current: TimeOfDayState = 'day';

  const apply = (state: TimeOfDayState): void => {
    const p = PRESETS[state];
    sun.intensity = p.sunIntensity;
    sun.diffuse = p.sunDiffuse;
    sun.direction = p.sunDirection.clone();
    ambient.intensity = p.ambientIntensity;
    ambient.diffuse = p.ambientDiffuse;
    scene.clearColor = p.clearColor;
    scene.fogColor = p.fogColor;
    current = state;
  };

  return {
    get: () => current,
    set: apply,
    cycle: () => {
      const next = ORDER[(ORDER.indexOf(current) + 1) % ORDER.length]!;
      apply(next);
    },
  };
}
